import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Phone, Video, PhoneIncoming, PhoneOutgoing, PhoneMissed, Loader2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCall } from '../../contexts/CallContext';
import { useGroupCall } from '../../contexts/GroupCallContext';
import { formatDateOnly, formatTimeOnly } from '../../utils/timezone';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'missed', label: 'Missed' },
  { key: 'voice', label: 'Voice' },
  { key: 'video', label: 'Video' },
];

const PAGE_SIZE = 30;

const durationLabel = (secs) => {
  const m = Math.floor((secs || 0) / 60);
  const s = (secs || 0) % 60;
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};

const outcomeLabel = (item) => {
  if (item.outcome === 'completed') return durationLabel(item.duration_seconds);
  if (item.outcome === 'connect_timeout') return "Couldn't connect";
  if (item.outcome === 'cancelled') return item.direction === 'outgoing' ? 'Cancelled' : 'Missed';
  if (item.outcome === 'declined') return item.direction === 'outgoing' ? 'Declined' : 'Missed';
  if (item.outcome === 'timeout') return item.direction === 'outgoing' ? 'No answer' : 'Missed';
  if (item.outcome === 'missed') return item.direction === 'outgoing' ? 'No answer' : 'Missed';
  return item.direction === 'outgoing' ? 'Not completed' : 'Missed';
};

const groupJoinersLabel = (item) => {
  const joined = (item.participants || []).filter(p => p.duration_seconds !== null && p.duration_seconds !== undefined);
  if (!joined.length) return null;
  return joined.map(p => `${p.name} ${durationLabel(p.duration_seconds)}`).join(' · ');
};

const timeLabel = (iso) => {
  // Project timezone utils — respects the user's configured timezone/hour cycle
  const time = formatTimeOnly(iso);
  const day = formatDateOnly(iso);
  if (day === formatDateOnly(new Date())) return time;
  return `${day} ${time}`;
};

export const CallHistoryPanel = ({ open, onOpenChange, onOpenConversation }) => {
  const { api } = useAuth();
  const { startCall } = useCall();
  const { startGroupCall } = useGroupCall();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async (activeFilter, skip) => {
    setLoading(true);
    try {
      const { data } = await api.get('/calls/history', { params: { filter: activeFilter, skip, limit: PAGE_SIZE } });
      setItems(prev => (skip === 0 ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
    } catch (e) {
      // list simply stays empty on failure
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) fetchPage(filter, 0);
  }, [open, filter, fetchPage]);

  const handleCallBack = (item, media) => {
    onOpenChange(false);
    if (onOpenConversation) onOpenConversation(item.conversation_id);
    if (item.is_group) startGroupCall({ id: item.conversation_id, name: item.peer_name }, media);
    else startCall({ id: item.conversation_id }, item.peer_name, media);
  };

  const handleRowClick = (item) => {
    onOpenChange(false);
    if (onOpenConversation) onOpenConversation(item.conversation_id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="call-history-dialog">
        <DialogHeader>
          <DialogTitle>Call History</DialogTitle>
          <DialogDescription>Your voice and video calls.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          {FILTERS.map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? 'default' : 'outline'}
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => setFilter(f.key)}
              data-testid={`call-history-filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="h-[55vh] overflow-y-auto -mx-2 px-2" data-testid="call-history-list">
          {items.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="call-history-empty">
              {filter === 'missed' ? 'No missed calls' : 'No calls yet'}
            </p>
          )}
          {items.map(item => {
            const MediaIcon = item.is_group ? Users : item.media === 'video' ? Video : Phone;
            const DirIcon = item.missed ? PhoneMissed : item.direction === 'outgoing' ? PhoneOutgoing : PhoneIncoming;
            const joiners = item.is_group ? groupJoinersLabel(item) : null;
            return (
              <div
                key={item.id}
                className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-accent/60 cursor-pointer"
                onClick={() => handleRowClick(item)}
                data-testid={`call-history-row-${item.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${item.missed ? 'bg-red-100 dark:bg-red-950' : 'bg-green-100 dark:bg-green-950'}`}>
                  <MediaIcon className={`w-4 h-4 ${item.missed ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${item.missed ? 'text-red-600' : ''}`}>{item.peer_name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <DirIcon className={`w-3 h-3 ${item.missed ? 'text-red-500' : ''}`} />
                    <span data-testid={`call-history-outcome-${item.id}`}>
                      {item.is_group ? `Group ${item.media === 'video' ? 'video' : 'voice'} · ` : ''}{outcomeLabel(item)}
                    </span>
                  </p>
                  {joiners && (
                    <p className="text-[11px] text-muted-foreground/80 truncate" title={joiners} data-testid={`call-history-joiners-${item.id}`}>
                      {joiners}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{timeLabel(item.created_at)}</span>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); handleCallBack(item, 'voice'); }}
                    title="Voice call"
                    data-testid={`call-history-callback-voice-${item.id}`}
                  >
                    <Phone className="w-4 h-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); handleCallBack(item, 'video'); }}
                    title="Video call"
                    data-testid={`call-history-callback-video-${item.id}`}
                  >
                    <Video className="w-4 h-4 text-green-600" />
                  </Button>
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          )}
          {!loading && items.length < total && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-1"
              onClick={() => fetchPage(filter, items.length)}
              data-testid="call-history-load-more"
            >
              Load more
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
